import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Grid } from '@material-ui/core';

import { Conditional } from '@klp/conditional';
import { Block, Typography } from '@klp/design';
import { Amount } from '@klp/design/Amount';
import { useKlpStyles } from '@klp/design/Theme';
import { FileDownload } from '@klp/filedownload';
import { isPdf } from '@klp/helpers';

import { fakturaApiRoutes } from '../api';
import type { Faktura } from '../classes';

const styles = () => ({
    title: {
        textTransform: 'uppercase' as const,
    },
    chip: {
        marginLeft: '0.5rem',
    },
    line: {
        display: 'flex',
        paddingTop: '0.25rem',
        paddingBottom: '0.25rem',
        '&:first-of-type': {
            marginTop: '-0.25rem',
        },
    },
    fakturaPreview: {
        '&:hover, &:active': {
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },
});

interface IProps {
    fakturaList: Array<Faktura>;
    sumUtestående: number;
    utestaaendeAntall: number;
}

export type IFakturaListSummary = IProps;

export const FakturaListSummary: React.FC<IFakturaListSummary> = (props) => {
    const { t } = useTranslation();
    const { fakturaList, sumUtestående, utestaaendeAntall } = props;
    const classes = useKlpStyles({}, styles);

    const renderFakturaRow = (faktura: Faktura) => {
        const { pdfFileName, statusCode, fakturaNummer, id } = faktura;
        return (
            <Block
                key={faktura.id}
                klpTypographyVariant="xsmall"
                className={classes.line}
            >
                {t('faktura.invoiceNumberShort')}
                &nbsp;
                {pdfFileName && isPdf(pdfFileName) && (
                    <FileDownload
                        path={fakturaApiRoutes.download(id)}
                        name={`${id}.pdf`}
                        preview={true}
                    >
                        <Grid
                            container={true}
                            justifyContent="space-between"
                            direction="row"
                        >
                            <Grid item={true}>
                                <div className={classes.fakturaPreview}>
                                    {fakturaNummer}
                                </div>
                            </Grid>
                            <Grid item={true}>
                                <div>
                                    <Chip
                                        label={t(`fakturaStatus.${statusCode}`)}
                                        variant="outlined"
                                        color="secondary"
                                        className={classes.chip}
                                        onClick={() => ({})}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    </FileDownload>
                )}
                {!(pdfFileName && isPdf(pdfFileName)) && (
                    <>
                        <span>{faktura.fakturaNummer}</span>
                        <Chip
                            label={t(`fakturaStatus.${statusCode}`)}
                            variant="outlined"
                            color="secondary"
                            className={classes.chip}
                        />
                    </>
                )}
            </Block>
        );
    };

    return (
        <Conditional
            condition={fakturaList && fakturaList.length > 0}
            // eslint-disable-next-line react/jsx-no-useless-fragment
            errorComponent={<></>}
        >
            <Grid
                container={true}
                direction="column"
                justifyContent="space-between"
            >
                <Grid item={true} xs={true}>
                    <Block marginTop="compact">
                        {fakturaList.length > 0 && (
                            <Typography
                                klpTypographyVariant="xsmall"
                                className={classes.title}
                            >
                                {t('faktura.notSettledInvoices', {
                                    count: fakturaList.length,
                                })}
                            </Typography>
                        )}
                        {fakturaList.map((faktura) =>
                            renderFakturaRow(faktura),
                        )}
                    </Block>
                </Grid>
                {sumUtestående > 0 && (
                    <Grid item={true}>
                        <Typography
                            paddingTop="compact"
                            className={classes.title}
                            klpTypographyVariant="xsmall"
                            translation="frontPage.invoice.outstanding"
                        />
                        <Typography
                            translation="frontPage.invoice.outstandingInvoiceCount"
                            translationOptions={{
                                outstandingInvoiceCount: utestaaendeAntall,
                            }}
                            klpTypographyVariant="small"
                        />
                        <Block klpTypographyVariant="small">
                            {t('frontPage.invoice.sumOutstanding')}
                            <Amount value={sumUtestående} />
                        </Block>
                    </Grid>
                )}
            </Grid>
        </Conditional>
    );
};

FakturaListSummary.displayName = 'FakturaListSummary';
